import { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Select } from 'antd';
import styled from 'styled-components';

export interface IRemoveIdmpTab {
  key: string;
  label: string;
  /** Live compositions, with the products each covers. */
  compositions: { id: string; title: string; idmpIds: string[] }[];
}

interface IProps {
  isOpen: boolean;
  /** Products on the ePI, already resolved to display names. */
  products: { id: string; name: string }[];
  tabs: IRemoveIdmpTab[];
  onConfirm: (idmpId: string) => void;
  onCancel: () => void;
}

/**
 * Takes a medicinal product off the ePI.
 *
 * The product leaves whichever composition covers it in each tab. Where it was
 * that document's only product the document goes with it — a composition
 * covering nothing is not valid — so the consequences are listed per tab
 * before confirming.
 */
const RemoveIdmpModal: React.FC<IProps> = ({
  isOpen,
  products,
  tabs,
  onConfirm,
  onCancel,
}) => {
  const [idmpId, setIdmpId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) setIdmpId(null);
  }, [isOpen]);

  const isLast = products.length <= 1;

  /** Per tab: the document covering the product, and whether it goes too. */
  const consequences = useMemo(() => {
    if (!idmpId) return [];
    return tabs.map(tab => {
      const host = tab.compositions.find(c => c.idmpIds.includes(idmpId));
      return {
        key: tab.key,
        label: tab.label,
        title: host?.title,
        dropped: !!host && host.idmpIds.length === 1,
      };
    });
  }, [tabs, idmpId]);

  const droppedDocs = consequences.filter(c => c.dropped);

  return (
    <Modal
      open={isOpen}
      title="Remove a medicinal product"
      okText="Remove"
      okButtonProps={{ danger: true, disabled: !idmpId || isLast }}
      cancelText="Cancel"
      onOk={() => idmpId && onConfirm(idmpId)}
      onCancel={onCancel}
      destroyOnHidden
    >
      {isLast ? (
        <Alert
          type="warning"
          showIcon
          message="This is the only product on the ePI."
          description="An ePI has to document at least one medicinal product. Remove the ePI itself instead."
        />
      ) : (
        <>
          <Description>
            The product leaves the document that covers it in every tab.
          </Description>

          <Field>
            <Label>Medicinal product</Label>
            <Select
              options={products.map(p => ({ value: p.id, label: p.name }))}
              value={idmpId ?? undefined}
              onChange={setIdmpId}
              placeholder="Select a product"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
            />
          </Field>

          {idmpId && (
            <>
              {consequences.map(c => (
                <Field key={c.key}>
                  <Label>{c.label}</Label>
                  <Fixed $danger={c.dropped}>
                    {c.title
                      ? c.dropped
                        ? `${c.title} — deleted, it covers no other product`
                        : c.title
                      : '—'}
                  </Fixed>
                </Field>
              ))}

              {droppedDocs.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message={`${droppedDocs.length} document${droppedDocs.length > 1 ? 's' : ''} will be deleted with it`}
                  description="Their sections and titles are lost. This cannot be undone once saved."
                />
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default RemoveIdmpModal;

const Description = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #4b576a;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #4b576a;
`;

const Fixed = styled.div<{ $danger?: boolean }>`
  font-size: 13px;
  padding: 5px 12px;
  background: ${({ $danger }) => ($danger ? '#fff1f0' : '#f5f5f5')};
  color: ${({ $danger }) => ($danger ? '#a8071a' : 'inherit')};
  border-radius: 6px;
`;
